import {
    useCallback, useEffect, useRef, useState,
} from 'react';
import { useQueryClient } from 'react-query';
import { RenderIf, Spinner } from 'react-rainbow-components';
import { hideAppSpinner, showAppNotification, showAppSpinner } from '@rainbow-modules/app';
import {
    canEditInvoice,
    invoiceEditRequiresChangeReason,
    invoiceSignatureState,
    invoiceToFormValues,
    isSignatureRequested,
    isSigningLinkActive,
    toInvoiceContentBody,
} from '../helpers';
import {
    InvoiceBuilderApiError,
    useInvoice,
    useInvoiceSigningSession,
    useInvoiceTemplates,
    usePatchInvoice,
    useUpdateInvoiceContent,
    useUpdateInvoiceInternalNote,
} from '../api/hooks';
import {
    INVOICE_CHANGE_REASON_REQUIRED,
    INVOICE_CONTENT_CONFLICT,
    INVOICE_CONTENT_LOCKED,
} from '../constants';
import {
    InvoiceBuilderTemplate,
    InvoiceCreateStatus,
    InvoiceSignatureState,
} from '../types';
import Fields, { LineDraft } from '../CreateInvoiceDrawer/fields';
import FormFooter, { DrawerMode } from '../CreateInvoiceDrawer/footer';
import Preview from '../CreateInvoiceDrawer/Preview';
import {
    Container,
    Content,
    FormContainer,
    PreviewPane,
    StyledDrawer,
    StyledUniversalForm,
} from '../CreateInvoiceDrawer/styled';
import { EmptyDescription, EmptyState } from '../styled';
import ChangeReasonModal from './ChangeReasonModal';

const FORM_ID = 'edit-invoice-form';

const updateErrorMessage = (error: unknown) => {
    if (!(error instanceof InvoiceBuilderApiError)) {
        return (error as Error)?.message || 'Please try again.';
    }
    if (error.code === INVOICE_CONTENT_LOCKED) {
        return 'This invoice can no longer be edited.';
    }
    if (error.code === INVOICE_CHANGE_REASON_REQUIRED) {
        return 'A reason is required to change this invoice.';
    }
    if (error.code === INVOICE_CONTENT_CONFLICT) {
        return 'This invoice changed while you were editing it. Close it and try again.';
    }
    return error.message;
};

interface EditInvoiceDrawerProps {
    isOpen?: boolean;
    organizationId: string;
    invoiceId?: string;
    lockedCustomerId?: string;
    onRequestClose: () => void;
    onUpdated?: () => void;
}

const EditInvoiceDrawer = ({
    isOpen,
    organizationId,
    invoiceId,
    lockedCustomerId,
    onRequestClose,
    onUpdated,
}: EditInvoiceDrawerProps) => {
    const queryClient = useQueryClient();
    const { mutateAsync: updateContent } = useUpdateInvoiceContent(organizationId);
    const { mutateAsync: updateInternalNote } = useUpdateInvoiceInternalNote(organizationId);
    const { mutateAsync: patchInvoice } = usePatchInvoice(organizationId);
    const [mode, setMode] = useState<DrawerMode>('form');
    const [pendingValues, setPendingValues] = useState<Record<string, unknown> | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const {
        data: invoiceResult,
        error: invoiceError,
        isFetched: invoiceFetched,
    } = useInvoice(organizationId, invoiceId, {
        enabled: Boolean(isOpen && organizationId && invoiceId),
    });
    const invoice = invoiceResult?.data;
    const signatureRequested = Boolean(invoice && isSignatureRequested(invoice.document.design));
    const mustConfirmSignature = Boolean(invoice && signatureRequested);
    const signingSession = useInvoiceSigningSession(
        organizationId,
        invoice?.id,
        Boolean(isOpen && mustConfirmSignature),
    );
    const {
        data: templatesData,
        isFetched: templatesFetched,
    } = useInvoiceTemplates(organizationId, {
        enabled: Boolean(isOpen && organizationId),
    });

    useEffect(() => {
        if (!isOpen) {
            setMode('form');
            setPendingValues(null);
            setIsSaving(false);
        }
    }, [isOpen]);

    const signaturePending = mustConfirmSignature && !signingSession.isFetched;
    const signingError = signingSession.error as InvoiceBuilderApiError | null;
    const signatureFailed = mustConfirmSignature
        && signingSession.isError
        && signingError?.status !== 404
        && signingError?.status !== 410;
    let editSignatureState: InvoiceSignatureState | 'none' | undefined;
    if (!invoice || signaturePending || signatureFailed) {
        editSignatureState = undefined;
    } else if (!signatureRequested) {
        editSignatureState = 'none';
    } else {
        editSignatureState = invoiceSignatureState(
            invoice,
            signingSession.data?.data.acceptance ?? null,
            isSigningLinkActive(signingSession.data?.data.expiresAt),
        );
    }
    const canEdit = Boolean(
        invoice && canEditInvoice(invoice.status, editSignatureState),
    );
    const liveTemplate = (templatesData?.data.templates || []).find(
        (template) => template.id === invoice?.templateId,
    );
    const formReady = Boolean(
        isOpen
        && invoice
        && templatesFetched
        && !signaturePending
        && !signatureFailed
        && canEdit,
    );

    const initialValuesRef = useRef<{
        id?: string;
        updatedAt?: string;
        values?: Record<string, unknown>;
    }>({});
    if (!formReady || !invoice) {
        initialValuesRef.current = {};
    } else if (initialValuesRef.current.id !== invoice.id) {
        initialValuesRef.current = {
            id: invoice.id,
            updatedAt: invoice.updatedAt,
            values: invoiceToFormValues(invoice, {
                templateName: liveTemplate?.name,
                templateVersionId: liveTemplate?.versionId,
            }),
        };
    }
    const initialValues = initialValuesRef.current.values;

    const blocksDraftRegression = useCallback((values: Record<string, unknown>) => {
        if (!invoice) return true;
        const nextStatus = values.status as InvoiceCreateStatus;
        const currentStatus = invoice.status === 'sent' || invoice.status === 'paid'
            ? invoice.status
            : 'draft';
        if (nextStatus === 'draft' && currentStatus !== 'draft') {
            showAppNotification({
                title: 'Could not update invoice',
                description: 'A sent invoice cannot be moved back to draft.',
                icon: 'error',
                timeout: 5000,
            });
            return true;
        }
        return false;
    }, [invoice]);

    const persistInvoice = useCallback(async (
        values: Record<string, unknown>,
        changeReason?: string,
    ) => {
        if (!invoice) return false;
        const nextStatus = values.status as InvoiceCreateStatus;
        const currentStatus = invoice.status === 'sent' || invoice.status === 'paid'
            ? invoice.status
            : 'draft';
        const nextInternalNote = String(values.internalNote || '').trim();
        const currentInternalNote = (
            invoice.internalNote || invoice.document.internalNote || ''
        ).trim();
        let contentSaved = false;
        showAppSpinner();
        try {
            await updateContent({
                invoiceId: invoice.id,
                body: toInvoiceContentBody({
                    template: values.template as InvoiceBuilderTemplate | undefined,
                    issuedOn: values.issuedOn as Date,
                    dueOn: values.dueOn as Date,
                    notes: values.notes,
                    lines: values.lines as LineDraft[],
                    billToCustomValues: (
                        values.billToCustomValues || {}
                    ) as Record<string, unknown>,
                    changeReason,
                }, initialValuesRef.current.updatedAt),
            });
            contentSaved = true;
            if (nextInternalNote !== currentInternalNote) {
                await updateInternalNote({
                    invoiceId: invoice.id,
                    body: { internalNote: nextInternalNote },
                });
            }
            if (nextStatus === 'sent' && currentStatus === 'draft') {
                await patchInvoice({
                    invoiceId: invoice.id,
                    body: { type: 'markSent' },
                });
            }
            if (nextStatus === 'paid' && currentStatus !== 'paid') {
                if (currentStatus === 'draft') {
                    await patchInvoice({
                        invoiceId: invoice.id,
                        body: { type: 'markSent' },
                    });
                }
                await patchInvoice({
                    invoiceId: invoice.id,
                    body: { type: 'markPaid' },
                });
            }
            showAppNotification({
                title: 'Invoice updated',
                description: 'Invoice saved.',
                icon: 'success',
                timeout: 4000,
            });
            await queryClient.invalidateQueries(['invoice-builder']);
            onUpdated?.();
            onRequestClose();
            return true;
        } catch (error) {
            const description = contentSaved
                ? `${updateErrorMessage(error)} The invoice content was saved.`
                : updateErrorMessage(error);
            showAppNotification({
                title: 'Could not update invoice',
                description,
                icon: 'error',
                timeout: 5000,
            });
            if (contentSaved) {
                await queryClient.invalidateQueries(['invoice-builder']);
                onUpdated?.();
                onRequestClose();
                return true;
            }
            return false;
        } finally {
            hideAppSpinner();
        }
    }, [
        invoice,
        onRequestClose,
        onUpdated,
        patchInvoice,
        queryClient,
        updateContent,
        updateInternalNote,
    ]);

    const handleSubmit = useCallback(async (values: Record<string, unknown>) => {
        if (!invoice || blocksDraftRegression(values)) return;
        if (invoiceEditRequiresChangeReason(invoice.status, editSignatureState)) {
            setPendingValues(values);
            return;
        }
        await persistInvoice(values);
    }, [
        blocksDraftRegression,
        editSignatureState,
        invoice,
        persistInvoice,
    ]);

    const handleReasonConfirm = useCallback(async (reason: string) => {
        if (!pendingValues || isSaving) return;
        setIsSaving(true);
        const saved = await persistInvoice(pendingValues, reason);
        setIsSaving(false);
        if (saved) setPendingValues(null);
    }, [isSaving, pendingValues, persistInvoice]);

    const showForm = formReady && Boolean(initialValues);
    const showBlocked = Boolean(
        invoice
        && invoiceFetched
        && templatesFetched
        && !signaturePending
        && !signatureFailed
        && !canEdit,
    );
    const title = invoice?.number ? `Edit ${invoice.number}` : 'Edit invoice';
    const showLoading = !invoiceError && !signatureFailed && !showForm && !showBlocked;

    return (
        <>
            <StyledDrawer
                isOpen={isOpen}
                header={mode === 'preview' ? 'Invoice preview' : title}
                slideFrom="right"
                hideCloseButton={mode === 'preview'}
                onRequestClose={() => {
                    if (mode === 'preview') return;
                    onRequestClose();
                }}
            >
                <RenderIf isTrue={isOpen}>
                    <Container>
                        <RenderIf isTrue={showLoading}>
                            <EmptyState>
                                <Spinner />
                            </EmptyState>
                        </RenderIf>
                        <RenderIf isTrue={Boolean(invoiceError)}>
                            <EmptyState>
                                <EmptyDescription>
                                    {invoiceError?.message || 'Invoice could not be loaded.'}
                                </EmptyDescription>
                            </EmptyState>
                        </RenderIf>
                        <RenderIf isTrue={!invoiceError && signatureFailed}>
                            <EmptyState>
                                <EmptyDescription>
                                    Could not confirm whether this invoice is signed.
                                </EmptyDescription>
                            </EmptyState>
                        </RenderIf>
                        <RenderIf isTrue={showBlocked}>
                            <EmptyState>
                                <EmptyDescription>
                                    This invoice can no longer be edited.
                                </EmptyDescription>
                            </EmptyState>
                        </RenderIf>
                        <RenderIf isTrue={showForm}>
                            <StyledUniversalForm
                                id={FORM_ID}
                                onSubmit={handleSubmit}
                                initialValues={initialValues}
                                keepDirtyOnReinitialize
                            >
                                <Content $hidden={mode === 'preview'}>
                                    <FormContainer>
                                        <Fields
                                            organizationId={organizationId}
                                            lockedCustomerId={lockedCustomerId}
                                            mode="edit"
                                        />
                                    </FormContainer>
                                </Content>
                                <RenderIf isTrue={mode === 'preview'}>
                                    <PreviewPane>
                                        <Preview
                                            organizationId={organizationId}
                                            lockedCustomerId={lockedCustomerId}
                                        />
                                    </PreviewPane>
                                </RenderIf>
                                <FormFooter
                                    formId={FORM_ID}
                                    mode={mode}
                                    submitButtonLabel="Update"
                                    onPreviewClick={() => setMode('preview')}
                                    onClosePreviewClick={() => setMode('form')}
                                    onCancelClick={onRequestClose}
                                />
                            </StyledUniversalForm>
                        </RenderIf>
                    </Container>
                </RenderIf>
            </StyledDrawer>
            <ChangeReasonModal
                isOpen={Boolean(pendingValues)}
                isSubmitting={isSaving}
                onRequestClose={() => {
                    if (isSaving) return;
                    setPendingValues(null);
                }}
                onConfirm={handleReasonConfirm}
            />
        </>
    );
};

export default EditInvoiceDrawer;
